// src/CategoryPage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import "./CategoryPage.css";
import { FaArrowLeft } from "react-icons/fa";
import { getAuthor, authorAvatarUrl } from "./utils/authorLookup";
import { getImageUrl, getThumbUrl } from "./utils/imageUrls";

// ✅ NEW: report & block
import ThreeDotsMenu from "./components/ThreeDotsMenu";
import ConfirmModal from "./components/ConfirmModal";
import useBlockedUsers from "./hooks/useBlockedUsers";
import TopMindsBadge from "./TopMindsBadge";

const PAGE_SIZE = 12;
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes (same as before)

// Set to false to go back to the old "download everything and filter" behavior.
const USE_SERVER_TAG_QUERY = true;

// In-memory (keeps Firestore Timestamps intact, unlike the old localStorage JSON copy)
const listCache = new Map(); // category -> { at, items, shown }

// ---- helpers ----------------------------------------------------------------

// Tags are matched case-insensitively. Firestore can't do that, so we ask for the
// common casings of the category name and then re-check case-insensitively below.
const tagVariants = (category) => {
  const c = category.trim();
  const lower = c.toLowerCase();
  const title = lower.replace(/\b\w/g, (ch) => ch.toUpperCase());
  const sentence = lower.charAt(0).toUpperCase() + lower.slice(1);
  return [...new Set([c, lower, c.toUpperCase(), title, sentence])];
};

const millis = (v) => {
  if (!v) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// Older ideas may have an email saved as authorName - never show that
const safeName = (n) => (n && !String(n).includes("@") ? n : "Anonymous");

async function fetchCategoryDocs(category) {
  const lower = category.toLowerCase();
  const matches = (data) =>
    (data.tags || []).some((tag) => typeof tag === "string" && tag.toLowerCase() === lower);

  let docs = [];
  let usedServerQuery = false;

  if (USE_SERVER_TAG_QUERY) {
    try {
      // Reads only the matching ideas instead of the whole collection
      const snap = await getDocs(
        query(collection(db, "postedIdeas"), where("tags", "array-contains-any", tagVariants(category)))
      );
      docs = snap.docs.filter((d) => matches(d.data()));
      usedServerQuery = true;
    } catch (e) {
      console.warn("Tag query failed, using fallback:", e);
    }
  }

  // Safety net: query failed or found nothing (e.g. unusual tag casing) -> old behavior
  if (!usedServerQuery || docs.length === 0) {
    const snap = await getDocs(collection(db, "postedIdeas"));
    docs = snap.docs.filter((d) => matches(d.data()));
  }
  return docs;
}

const toItem = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    imageUrl: getImageUrl(data.imagePath) || "",   // full size for the detail page
    thumbUrl: getThumbUrl(data.imagePath) || "",   // small for this list
  };
};

// Fills in author name + avatar + Top Minds flag (shared lookup: one read per author, cached)
const hydrateAuthors = (list) =>
  Promise.all(
    list.map(async (idea) => {
      const author = idea.uid ? await getAuthor(idea.uid) : null;
      return {
        ...idea,
        authorName: (author && author.name) || safeName(idea.authorName),
        profileImageUrl: authorAvatarUrl(author) || idea.profileImageUrl || "",
        authorTopMinds: !!(author && author.topMinds),
      };
    })
  );

// ---- component ---------------------------------------------------------------

const CategoryPage = () => {
  const { category } = useParams();
  const [items, setItems] = useState([]);       // all matches, newest first
  const [shown, setShown] = useState(PAGE_SIZE); // how many are visible
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();

  // ✅ NEW: current user + block system
  const user = getAuth().currentUser;
  const { blockedIds, blockUser } = useBlockedUsers();
  const [blockTarget, setBlockTarget] = useState(null);
  const [blocking, setBlocking] = useState(false);
  const [blockError, setBlockError] = useState("");

  const handleBlock = (uid) => {
    setBlockError("");
    setBlockTarget(uid);
  };

  const confirmBlock = async () => {
    try {
      setBlocking(true);
      await blockUser(blockTarget);
      setBlockTarget(null);
    } catch (e) {
      console.error("Block failed:", e);
      setBlockError("Couldn't block this user. Please try again.");
    } finally {
      setBlocking(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const cached = listCache.get(category);
    if (cached && Date.now() - cached.at < CACHE_TTL) {
      setItems(cached.items);
      setShown(cached.shown);
      setLoading(false);
      return undefined;
    }

    const run = async () => {
      try {
        setLoading(true);
        const docs = await fetchCategoryDocs(category);

        // Newest first
        const sorted = docs.map(toItem).sort((a, b) => millis(b.createdAt) - millis(a.createdAt));

        // Only the first page gets author lookups now; the rest on "Load more"
        const firstPage = await hydrateAuthors(sorted.slice(0, PAGE_SIZE));
        const all = [...firstPage, ...sorted.slice(PAGE_SIZE)];

        listCache.set(category, { at: Date.now(), items: all, shown: PAGE_SIZE });
        if (!cancelled) {
          setItems(all);
          setShown(PAGE_SIZE);
        }
      } catch (error) {
        console.error("Error fetching ideas:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [category]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const start = shown;
      const hydrated = await hydrateAuthors(items.slice(start, start + PAGE_SIZE));
      const next = [...items];
      hydrated.forEach((h, i) => {
        next[start + i] = h;
      });
      setItems(next);
      setShown(start + PAGE_SIZE);

      const entry = listCache.get(category);
      if (entry) {
        entry.items = next;
        entry.shown = start + PAGE_SIZE;
      }
    } finally {
      setLoadingMore(false);
    }
  };

  // ✅ hide blocked users (done at render time so the cache never shows them)
  const visibleIdeas = items.slice(0, shown).filter((idea) => !blockedIds.includes(idea.uid));
  const hasMore = shown < items.length;

  return (
    <div className="category-container">
      <div className="category-header">
        <FaArrowLeft className="leaderboard-back-icon" onClick={() => navigate(-1)} />
        <h2 className="category-title">{category.toUpperCase()}</h2>
      </div>
      {loading && visibleIdeas.length === 0 ? (
        <p>Loading...</p>
      ) : visibleIdeas.length > 0 ? (
        <div className="idea-list">
          {visibleIdeas.map((idea, idx) => (
            <div key={idea.id} className="idea-card" onClick={() => navigate(`/view-idea/${idea.id}`, { state: { idea } })}>
              <div className="idea-top">
                <div className="idea-image-section">
                  {/* ✅ user row + three dots in the corner */}
                  <div className="cat-user-row" style={idea.authorTopMinds ? { flexWrap: 'wrap' } : undefined}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); navigate(`/journey/${idea.uid}`); }}>
                      {idea.profileImageUrl ? (
                        <img src={idea.profileImageUrl} alt={idea.authorName} className="profile-image" />
                      ) : (
                        <span className="avatar">{idea.authorName?.[0] || "?"}</span>
                      )}
                      <span className="user-name-category">{idea.authorName || "Anonymous"}</span>
                    </div>

                    <ThreeDotsMenu
                      type="idea"
                      id={idea.id}
                      ownerId={idea.uid}
                      currentUid={user?.uid}
                      onBlock={handleBlock}
                    />

                    {idea.authorTopMinds && (
                        <div style={{ flex: '0 0 100%', marginTop: '6px' }}>
                          <TopMindsBadge />
                        </div>
                      )}
                  </div>
                  {(idea.thumbUrl || idea.imageUrl) && (
                    <img
                      src={idea.thumbUrl || idea.imageUrl}
                      alt={idea.title}
                      className="idea-image"
                      loading={idx < 3 ? "eager" : "lazy"}
                      fetchpriority={idx < 3 ? "high" : "auto"}
                    />
                  )}
                </div>
                <div className="idea-meta">
                  <h3>{idea.title}</h3>
                  <div className="tags">
                    {idea.tags?.map((t, tagIdx) => (
                      <span className="tag" key={tagIdx}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>
              <p className="idea-description idea-description-truncated">{idea.description}</p>
            </div>
          ))}

          {hasMore && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              style={{
                display: "block",
                margin: "16px auto",
                padding: "10px 24px",
                borderRadius: "20px",
                border: "1px solid currentColor",
                background: "transparent",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          )}
        </div>
      ) : (
        <p>No ideas found in this category.</p>
      )}

      {/* ✅ in-app block confirmation */}
      <ConfirmModal
        open={!!blockTarget}
        title="Block this user?"
        message="You won't see their ideas anymore. You can't undo this from here yet."
        confirmText="Block"
        danger
        loading={blocking}
        error={blockError}
        onConfirm={confirmBlock}
        onCancel={() => setBlockTarget(null)}
      />
    </div>
  );
};

export default CategoryPage;